import React, { ReactNode, useState } from 'react';
import classNames from 'classnames';
import * as css from './checkableInput.module.scss';
import Icon, { IconType } from '../icon';

type RadioGroupProps = {
  children: ReactNode;
  label: string;
  className?: string;
};

export const RadioGroup: React.FC<React.PropsWithChildren<RadioGroupProps>> = ({
  children,
  className,
  label,
}) => (
  <div
    role="radiogroup"
    className={classNames(className, css.radioGroupWrapper)}
  >
    <p className={css.label}>{label}</p>
    <div className={css.radioGroup}>{children}</div>
  </div>
);

type RadioButtonProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'type'
> & {
  id: string;
  label?: string;
  wrapperClassname?: string;
  handleChange: (id: string) => void;
};

export const RadioButton: React.FC<
  React.PropsWithChildren<RadioButtonProps>
> = ({
  id,
  name,
  value,
  label,
  checked,
  disabled,
  tabIndex,
  handleChange,
  wrapperClassname,
  ...props
}) => {
  const [isFocused, setIsFocused] = useState<boolean>(false);

  const handleRadioChange = (event: React.SyntheticEvent) => {
    event.preventDefault();
    if (disabled) return;

    value && handleChange(value.toString());
  };

  const handleKeyEvent = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') handleRadioChange(event);
  };

  return (
    <div
      className={classNames(
        css.checkableInputWrapper,
        wrapperClassname,
        disabled && css.disabled,
      )}
      onClick={handleRadioChange}
    >
      <Icon
        className={classNames(
          css.icon,
          checked ? css.checked : css.unchecked,
          isFocused && css.isFocused,
          disabled && css.disabled,
        )}
        type={checked ? IconType.CheckCircle : IconType.RadioButtonUnchecked}
      />
      <input
        type="radio"
        id={id}
        name={name}
        onKeyDown={handleKeyEvent}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        disabled={disabled}
        checked={checked}
        onChange={() => {
          /* Method is not to be used, but must be defined. Key events should be put directly on input and mouse events on wrapper */
        }}
        value={value}
        {...props}
      />
      <label htmlFor={id} tabIndex={disabled ? undefined : tabIndex}>
        {label}
      </label>
    </div>
  );
};
